import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from "react";

interface IChatWidgetPresence {
  /** True while at least one chat widget is mounted, fixed or inline. */
  hasChatWidget: boolean;
  /** Registers a mounted chat widget; returns the unregister callback. */
  registerChatWidget: () => () => void;
}

const noop = () => () => undefined;

const ChatWidgetPresenceContext = createContext<IChatWidgetPresence>({
  hasChatWidget: false,
  registerChatWidget: noop,
});

export const useChatWidgetPresence = (): IChatWidgetPresence =>
  useContext(ChatWidgetPresenceContext);

export function ChatWidgetPresenceProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [count, setCount] = useState(0);

  const registerChatWidget = useCallback(() => {
    setCount((current) => current + 1);

    return () => setCount((current) => Math.max(0, current - 1));
  }, []);

  const contextValue = useMemo(
    () => ({
      hasChatWidget: count > 0,
      registerChatWidget,
    }),
    [count, registerChatWidget]
  );

  return (
    <ChatWidgetPresenceContext.Provider value={contextValue}>
      {children}
    </ChatWidgetPresenceContext.Provider>
  );
}
